import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Combobox,
  Divider,
  DropdownSelect,
  Flex,
  FormField,
  FormHelp,
  IconArrowRightOutline,
  Label,
  Spacer,
  TextButton,
  ToggleButton,
} from '@zillow/constellation';
import type Constellation from '@zillow/constellation';
import { addWeeks, format, isBefore, parse } from 'date-fns';
import { RecurringFrequency, TourDuration } from '@zg-rentals/ts-z-calendar-client';
import { Alert } from '@zillow/constellation-10';
type DatePickerMetaProps = Constellation.DatePickerMetaProps;
type ComboboxMeta = Constellation.ComboboxMeta;

import type {
  AvailabilityEvent,
  AvailabilityEventWeeklyRecurringDaysMap,
  OnCalendarEventSaveClickParams,
  RecurringFrequencyLabels,
  RecurringFrequencyValues,
  WeeklyRecurringDays,
} from '../../../../constants/TourCalendar';
import {
  DefaultSelectedWeeklyRecurringDays,
  HOUR_MINUTE_MERIDIEM_FORMAT_STRING,
  RecurringFrequencyLabelToValueMap,
  RecurringFrequencyNeverValue,
  RecurringFrequencyValueLabels,
  RecurringWeeklyButtonsValueLabels,
  TOUR_DURATION_NUMBER_MAP,
} from '../../../../constants/TourCalendar';
import FormattedDatePickerInput from '../FormattedDatePickerInput';
import {
  calculateEndTimeWithPreservedDuration,
  convert12HourTimeTo24HourTime,
  createAvailabilityTimeOptions,
  isStartAndEndTimeFullHourBlock,
  isTimeBeforeOrEqual,
} from '../../../../utils/availabilityTimeUtils';
import AdornedComboboxInput from '../../../AdornedComboboxInput';
import ReccuringAvailabilityConfirmationModal from '../RecuringAvailabilityConfirmationModal';
import { formatTimeString, setTimeForDate } from '../../../../utils/formatTime';
import { FULL_HOUR_BLOCK_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';

const getTimeslotValidationError = ({
  selectedStartTime,
  selectedEndTime,
  tourDuration,
}: {
  selectedStartTime: string;
  selectedEndTime: string;
  tourDuration: TourDuration;
}) => {
  if (!selectedStartTime || !selectedEndTime) {
    return 'Enter a start and end time';
  }

  if (isTimeBeforeOrEqual(selectedEndTime, selectedStartTime)) {
    return 'End time must be after start time';
  }

  // 60 minute tours must have a duration that is a multiple of 60 minutes
  const parsedSelectedStartTime = parse(selectedStartTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());
  const parsedSelectedEndTime = parse(selectedEndTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING, new Date());
  if (
    tourDuration === TourDuration.MINUTES_60 &&
    !isStartAndEndTimeFullHourBlock(parsedSelectedStartTime, parsedSelectedEndTime, tourDuration)
  ) {
    return FULL_HOUR_BLOCK_ERROR_MESSAGE;
  }
};

const getRecurringFormValidationError = ({
  selectedEventDate,
  selectedFrequency,
  selectedWeeklyRecurringDays,
  selectedRepeatUntilDate,
}: {
  selectedEventDate: string;
  selectedFrequency: RecurringFrequencyValues;
  selectedWeeklyRecurringDays: AvailabilityEventWeeklyRecurringDaysMap;
  selectedRepeatUntilDate: string;
}) => {
  const isRecurringWeeklySelected = selectedFrequency === RecurringFrequency.WEEKLY;
  const hasNoRecurringDaysSelected = !Object.values(selectedWeeklyRecurringDays).some(
    (isDayRecurring) => isDayRecurring,
  );

  if (isRecurringWeeklySelected && hasNoRecurringDaysSelected) {
    return 'Select at least one recurring day';
  }

  if (isRecurringWeeklySelected && isBefore(new Date(selectedRepeatUntilDate), new Date(selectedEventDate))) {
    return 'Date must be after event date.';
  }

  return null;
};

export type AvailabilityEventEditPopoverBodyProps = {
  event: AvailabilityEvent;
  onClose: () => void;
  onSaveClick: (params: OnCalendarEventSaveClickParams) => void;
  canEditTourDuration: boolean;
  tourDuration: TourDuration;
};

const AvailabilityEventEditPopoverBody = ({
  event,
  onClose,
  onSaveClick,
  canEditTourDuration,
  tourDuration,
}: AvailabilityEventEditPopoverBodyProps) => {
  const [selectedEventDate, setSelectedEventDate] = useState(format(event.start, 'MM/dd/yyyy'));
  const [selectedStartTime, setSelectedStartTime] = useState(formatTimeString(event.start, { showMeridiem: true }));
  const [selectedEndTime, setSelectedEndTime] = useState(formatTimeString(event.end, { showMeridiem: true }));
  const [selectedFrequency, setSelectedFrequency] = useState<RecurringFrequencyValues>(RecurringFrequencyNeverValue);
  const [selectedWeeklyRecurringDays, setSelectedWeeklyRecurringDays] =
    useState<AvailabilityEventWeeklyRecurringDaysMap>(DefaultSelectedWeeklyRecurringDays);
  const [selectedRepeatUntilDate, setSelectedRepeatUntilDate] = useState(format(addWeeks(new Date(), 3), 'MM/dd/yyyy'));
  const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);

  const timeslotValidationError = useMemo(
    () => getTimeslotValidationError({ selectedStartTime, selectedEndTime, tourDuration }),
    [selectedStartTime, selectedEndTime, tourDuration],
  );
  const recurringFormValidationError = useMemo(
    () =>
      getRecurringFormValidationError({
        selectedEventDate,
        selectedFrequency,
        selectedWeeklyRecurringDays,
        selectedRepeatUntilDate,
      }),
    [selectedEventDate, selectedFrequency, selectedWeeklyRecurringDays, selectedRepeatUntilDate],
  );

  const startAvailabilityTimeOptions = useMemo(() => createAvailabilityTimeOptions(tourDuration), [tourDuration]);
  const endAvailabilityTimeOptions = useMemo(
    () => createAvailabilityTimeOptions(tourDuration, convert12HourTimeTo24HourTime(selectedStartTime)),
    [selectedStartTime, tourDuration],
  );

  const isRecurringWeeklySelected = selectedFrequency === RecurringFrequency.WEEKLY;
  const isSaveDisabled = Boolean(timeslotValidationError) || Boolean(recurringFormValidationError);

  useEffect(() => {
    if (event.recurringDays) {
      setSelectedWeeklyRecurringDays(event.recurringDays);
    }
    if (event.frequency) {
      setSelectedFrequency(event.frequency);
    }
    if (event.recurrenceEndDateMs) {
      setSelectedRepeatUntilDate(format(new Date(event.recurrenceEndDateMs), 'MM/dd/yyyy'));
    }
  }, [event]);

  useEffect(() => {
    if (!event.isRecurringEvent && isRecurringWeeklySelected) {
      const eventDayName = format(parse(selectedEventDate, 'MM/dd/yyyy', new Date()), 'EEEE').toUpperCase();
      setSelectedWeeklyRecurringDays({ ...DefaultSelectedWeeklyRecurringDays, [eventDayName]: true });
    }
  }, [event, isRecurringWeeklySelected]);

  const onSaveClickHandler = () => {
    onSaveClick({
      selectedStartTime: convert12HourTimeTo24HourTime(selectedStartTime),
      selectedEndTime: convert12HourTimeTo24HourTime(selectedEndTime),
      selectedEventDate,
      selectedFrequency: selectedFrequency !== RecurringFrequencyNeverValue ? selectedFrequency : undefined,
      selectedWeeklyRecurringDays,
      selectedRepeatUntilDate,
    });
  };

  const handleSaveClick = () => {
    if (event.isRecurringEvent) {
      return setDisplayConfirmationModal(true);
    }

    onSaveClickHandler();
  };

  const onChangeStartTime: (startTime: string | Array<string>, meta: ComboboxMeta) => void = (startTime) => {
    if (typeof startTime === 'string') {
      const originalStartTime = setTimeForDate(
        new Date(selectedEventDate),
        convert12HourTimeTo24HourTime(selectedStartTime),
      );
      const originalEndTime = setTimeForDate(
        new Date(selectedEventDate),
        convert12HourTimeTo24HourTime(selectedEndTime),
      );
      const newStartTime = setTimeForDate(new Date(selectedEventDate), convert12HourTimeTo24HourTime(startTime));

      const newEndTime = calculateEndTimeWithPreservedDuration({
        originalStart: originalStartTime,
        originalEnd: originalEndTime,
        newStart: newStartTime,
        targetDate: new Date(selectedEventDate),
      });

      if (newEndTime) {
        setSelectedEndTime(format(newEndTime, HOUR_MINUTE_MERIDIEM_FORMAT_STRING));
      }

      setSelectedStartTime(startTime);
    }
  };

  const onChangeEndTime: (option: string | Array<string>, meta: ComboboxMeta) => void = (option) => {
    if (typeof option === 'string') {
      setSelectedEndTime(option);
    }
  };

  const onChangeEventDate = (value: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }
    setSelectedEventDate(value);
    // Reset the weekly recurring days when event date changes
    const eventDayName = format(parse(value, 'MM/dd/yyyy', new Date()), 'EEEE').toUpperCase();
    setSelectedWeeklyRecurringDays({ ...DefaultSelectedWeeklyRecurringDays, [eventDayName]: true });
  };

  const onChangeFrequency = (value: RecurringFrequencyLabels) => {
    if (value) {
      setSelectedFrequency(RecurringFrequencyLabelToValueMap[value]);
    }
  };

  const onClickWeeklyRecurringDays = (value: WeeklyRecurringDays) => {
    setSelectedWeeklyRecurringDays({ ...selectedWeeklyRecurringDays, [value]: !selectedWeeklyRecurringDays[value] });
  };

  const onChangeRepeatUntilDate = (value: string, meta: DatePickerMetaProps) => {
    if (meta.reason === 'manual input') {
      return;
    }
    setSelectedRepeatUntilDate(value);
  };

  return (
    <>
      <div data-testid="AvailabilityEventEditPopoverBody">
        <FormField
          label={<Label>Date</Label>}
          control={
            <FormattedDatePickerInput
              date={selectedEventDate}
              onChangeDate={onChangeEventDate}
              dataTestId="AvailabilityEventEditPopoverBody-EventDate-Input"
            />
          }
          marginBottom="sm"
          data-testid="AvailabilityEventEditPopoverBody-DatePicker"
        />
        <Spacer marginBottom="sm">
          <Flex display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <FormField
              label={<Label>From</Label>}
              control={
                <Combobox
                  options={startAvailabilityTimeOptions}
                  value={selectedStartTime}
                  onChange={onChangeStartTime}
                  renderInput={AdornedComboboxInput}
                  data-testid="AvailabilityEventEditPopoverBody-StartTime-Input"
                />
              }
              data-testid="AvailabilityEventEditPopoverBody-StartTime"
            />
            <Spacer paddingTop="md" marginX="xs">
              <IconArrowRightOutline size="sm" />
            </Spacer>
            <FormField
              label={<Label>To</Label>}
              control={
                <Combobox
                  options={endAvailabilityTimeOptions}
                  value={selectedEndTime}
                  onChange={onChangeEndTime}
                  renderInput={AdornedComboboxInput}
                  data-testid="AvailabilityEventEditPopoverBody-EndTime-Input"
                />
              }
              data-testid="AvailabilityEventEditPopoverBody-EndTime"
            />
          </Flex>
          {timeslotValidationError && (
            <FormHelp error marginTop="xs" data-testid="AvailabilityEventEditPopoverBody-Error">
              {timeslotValidationError}
            </FormHelp>
          )}
          {canEditTourDuration && (
            <Alert
              role="status"
              tone="info"
              css={{ marginTop: 'default' }}
              data-testid="AvailabilityEventEditPopoverBody-TourDurationAlert"
            >
              Tour length is set to {TOUR_DURATION_NUMBER_MAP[tourDuration]} min.
            </Alert>
          )}
        </Spacer>
        <FormField
          label={<Label>Repeat</Label>}
          control={
            <DropdownSelect
              options={RecurringFrequencyValueLabels}
              value={selectedFrequency}
              // @ts-ignore
              onChange={onChangeFrequency}
              showLabelForValue={true}
              data-testid="AvailabilityEventEditPopoverBody-RepeatDropdown"
            />
          }
        />

        {isRecurringWeeklySelected && (
          <ButtonGroup
            aria-label="Toggle button group for each day of the week"
            marginY="sm"
            data-testid="AvailabilityEventEditPopoverBody-WeeklyRecurringDays"
          >
            {RecurringWeeklyButtonsValueLabels.map((option) => (
              <ToggleButton
                key={option.value}
                pressed={selectedWeeklyRecurringDays[option.value]}
                onClick={() => onClickWeeklyRecurringDays(option.value)}
                aria-label={option.value}
                data-testid={`AvailabilityEventEditPopoverBody-WeeklyRecurringDays-${option.value}`}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ButtonGroup>
        )}
        {selectedFrequency ? (
          <FormField
            label={<Label>Repeat until</Label>}
            control={
              <FormattedDatePickerInput
                date={selectedRepeatUntilDate}
                onChangeDate={onChangeRepeatUntilDate}
                dataTestId="AvailabilityEventEditPopoverBody-RepeatUntilDate-Input"
              />
            }
            error={Boolean(recurringFormValidationError)}
            description={
              <FormHelp>Select a date up to three weeks in the future. {recurringFormValidationError}</FormHelp>
            }
            marginY="sm"
            data-testid="AvailabilityEventEditPopoverBody-RepeatUntilDate"
          />
        ) : null}

        <Divider marginY="sm" />
        <Flex as={ButtonGroup} display="flex" justifyContent="flex-end" aria-label="edit event form buttons">
          <TextButton onClick={onClose} data-testid="AvailabilityEventEditPopoverBody-CancelBtn">
            Cancel
          </TextButton>
          <Button
            buttonType="primary"
            onClick={handleSaveClick}
            disabled={isSaveDisabled}
            data-testid="AvailabilityEventEditPopoverBody-SaveBtn"
          >
            Add to listing
          </Button>
        </Flex>
      </div>

      {displayConfirmationModal && (
        <ReccuringAvailabilityConfirmationModal
          isForDelete={false}
          onClose={() => setDisplayConfirmationModal(false)}
          onConfirm={onSaveClickHandler}
        />
      )}
    </>
  );
};

export default AvailabilityEventEditPopoverBody;
